/** Visual layout constants for the responsive backgammon board. */
export const CHECKER_COLUMN_FILL = 0.94;
export const CHECKER_EDGE_INSET = 1;

export interface BackgammonDiceStripLayout {
  dieSize: number;
  gap: number;
  width: number;
  centers: ReadonlyArray<{ x: number; y: number }>;
}

export interface BackgammonDiceStripOptions {
  count: number;
  checkerDiameter: number;
  halfX: number;
  halfWidth: number;
  centerY: number;
  emphasized: boolean;
}

export interface OutTrayStackLayout {
  pieceWidth: number;
  pieceHeight: number;
  step: number;
  /** Center distance from the owner's outer field edge, growing inward toward the center seam. */
  centerOffsets: ReadonlyArray<number>;
}

export interface CheckerStackHitArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

/**
 * Places a horizontal dice strip inside one playable half. Local-turn dice are deliberately larger;
 * both variants shrink as needed so doubles (four dice) stay clear of the center bar and side trays.
 */
export function backgammonDiceStrip(options: BackgammonDiceStripOptions): BackgammonDiceStripLayout {
  const count = Math.max(0, Math.floor(options.count));
  const preferredSize = options.emphasized
    ? clamp(Math.round(options.checkerDiameter * 0.9), 64, 84)
    : clamp(Math.round(options.checkerDiameter * 0.7), 48, 64);
  if (count === 0) return { dieSize: preferredSize, gap: 0, width: 0, centers: [] };

  const availableWidth = Math.max(24, options.halfWidth - 32);
  let gap = count > 1 ? Math.max(8, Math.round(preferredSize * 0.14)) : 0;
  const fitSize = Math.floor((availableWidth - gap * (count - 1)) / count);
  const dieSize = Math.max(24, Math.min(preferredSize, fitSize));
  if (count > 1) {
    gap = Math.min(gap, Math.max(0, (availableWidth - dieSize * count) / (count - 1)));
  }

  const width = dieSize * count + gap * (count - 1);
  const firstCenterX = options.halfX + (options.halfWidth - width) / 2 + dieSize / 2;
  const centers = Array.from({ length: count }, (_, index) => ({
    x: firstCenterX + index * (dieSize + gap),
    y: options.centerY,
  }));
  return { dieSize, gap, width, centers };
}

/**
 * Center-to-center spacing for a point stack. Shallow stacks overlap just enough to read as one pile;
 * deeper stacks compress exactly as much as needed to remain inside the triangle.
 */
export function pointStackStep(diameter: number, stackRegion: number, count: number): number {
  if (count <= 1) return 0;
  return Math.min(diameter * 0.86, Math.max(0, stackRegion) / (count - 1));
}

/** Center-to-center spacing for checkers on one side of the center bar. */
export function waitStackStep(diameter: number, halfFieldHeight: number, count: number): number {
  if (count <= 1) return 0;
  const region = Math.max(0, halfFieldHeight - diameter * 1.5 - CHECKER_EDGE_INSET);
  return Math.min(diameter * 0.55, region / (count - 1));
}

/**
 * Edge-on checker geometry for one player's half of the shared bearing-off tray. Each piece remains
 * individually visible at normal board sizes; on an unusually short field the step compresses just
 * enough to keep all 15 pieces on the owner's side of the center seam.
 */
export function outTrayStackLayout(
  checkerDiameter: number,
  trayWidth: number,
  halfFieldHeight: number,
  count: number,
  inset = 6,
): OutTrayStackLayout {
  const normalizedCount = Math.max(0, Math.floor(count));
  const pieceWidth = Math.max(12, Math.min(Math.round(checkerDiameter * 0.82), trayWidth - inset * 2));
  const pieceHeight = clamp(Math.round(checkerDiameter * 0.18), 8, 16);
  const preferredGap = Math.max(1, Math.round(pieceHeight * 0.14));
  const availableStepRegion = Math.max(0, halfFieldHeight - pieceHeight - inset * 2);
  const step = normalizedCount <= 1
    ? 0
    : Math.min(pieceHeight + preferredGap, availableStepRegion / (normalizedCount - 1));
  const firstCenter = inset + pieceHeight / 2;
  const centerOffsets = Array.from({ length: normalizedCount }, (_, index) => firstCenter + index * step);
  return { pieceWidth, pieceHeight, step, centerOffsets };
}

/** The full on-screen half of the shared tray owned by the local player. */
export function bearingOffTrayHitArea(
  trayX: number,
  trayWidth: number,
  centerSeamY: number,
  outerEdgeY: number,
): CheckerStackHitArea {
  return {
    x: trayX,
    y: Math.min(centerSeamY, outerEdgeY),
    width: Math.max(0, trayWidth),
    height: Math.abs(outerEdgeY - centerSeamY),
  };
}

/** A single touch target covering every visible checker in a vertical point/bar stack. */
export function checkerStackHitArea(
  centerX: number,
  firstCenterY: number,
  lastCenterY: number,
  checkerDiameter: number,
  columnWidth: number,
  padding = 6,
): CheckerStackHitArea {
  const width = Math.max(checkerDiameter, Math.min(columnWidth, checkerDiameter + padding * 2));
  const top = Math.min(firstCenterY, lastCenterY) - checkerDiameter / 2 - padding;
  return {
    x: centerX - width / 2,
    y: top,
    width,
    height: Math.abs(lastCenterY - firstCenterY) + checkerDiameter + padding * 2,
  };
}
